/**
 * Keyless Google Cloud setup for an organization.
 *
 * An org admin runs the script below once in Google Cloud Shell. It creates a
 * deploy service account and a Workload Identity pool that trusts GitHub
 * Actions — but only from the organization's own GitHub accounts — so a
 * project's workflows can deploy without any stored key. CompBox keeps only
 * the identifiers.
 */
export const GCP_REGIONS = [
    "us-central1",
    "us-east1",
    "us-east4",
    "us-west1",
    "us-west2",
    "northamerica-northeast1",
    "europe-west1",
    "europe-west2",
    "europe-west3",
    "europe-west4",
    "asia-south1",
    "asia-southeast1",
    "asia-northeast1",
    "australia-southeast1",
];
export const GCP_DEPLOY_ACCOUNT = "compbox-deploy";
export const GCP_IDENTITY_POOL = "compbox";
export const GCP_IDENTITY_PROVIDER = "github";
/** Roles the deploy account gets on the project. */
export const GCP_DEPLOY_ROLES = [
    "roles/run.admin",
    "roles/artifactregistry.admin",
    "roles/iam.serviceAccountUser",
    "roles/iam.serviceAccountAdmin",
    "roles/storage.admin",
    "roles/serviceusage.serviceUsageConsumer",
    // Keeps each environment's DATABASE_URL in Secret Manager and lets its runtime account read it.
    "roles/secretmanager.admin",
    // Creates each environment's Cloud SQL database (ENG-316) and connects the migrate job to it.
    "roles/cloudsql.admin",
];
/** APIs the project needs: Cloud Run, its image registry, keyless sign-in, state, secrets and Cloud SQL. */
export const GCP_SERVICES = [
    "run.googleapis.com",
    "artifactregistry.googleapis.com",
    "iam.googleapis.com",
    "iamcredentials.googleapis.com",
    "sts.googleapis.com",
    "cloudresourcemanager.googleapis.com",
    "storage.googleapis.com",
    "secretmanager.googleapis.com",
    "sqladmin.googleapis.com",
    "cloudbuild.googleapis.com",
];
/**
 * A Cloud SQL environment's runtime account needs Cloud SQL Client on the
 * project. The deploy account may grant that role, and only that role.
 */
export const GCP_DEPLOY_CONDITIONAL_ROLE = {
    role: "roles/resourcemanager.projectIamAdmin",
    condition: {
        title: "compbox-cloudsql-client-only",
        description: "CompBox deploys may only grant Cloud SQL Client.",
        expression: "api.getAttribute('iam.googleapis.com/modifiedGrantsByRole', []).hasOnly(['roles/cloudsql.client'])",
    },
};
const PROJECT_ID = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;
const PROJECT_NUMBER = /^\d{6,20}$/;
const GITHUB_LOGIN = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,37}[A-Za-z0-9])?$/;
export function isGcpProjectId(value) {
    return PROJECT_ID.test(value);
}
export function isGcpProjectNumber(value) {
    return PROJECT_NUMBER.test(value);
}
export function isGcpRegion(value) {
    return GCP_REGIONS.includes(value);
}
export function isGitHubLogin(value) {
    return GITHUB_LOGIN.test(value);
}
export function gcpServiceAccountEmail(projectId) {
    return `${GCP_DEPLOY_ACCOUNT}@${projectId}.iam.gserviceaccount.com`;
}
export function gcpWorkloadProvider(projectNumber) {
    return `projects/${projectNumber}/locations/global/workloadIdentityPools/${GCP_IDENTITY_POOL}/providers/${GCP_IDENTITY_PROVIDER}`;
}
/**
 * The Workload Identity provider's attribute condition: only GitHub Actions
 * runs from these accounts may sign in. Duplicates are dropped, order kept.
 */
export function gcpAttributeCondition(githubOwners) {
    const owners = [...new Set(githubOwners)];
    return `assertion.repository_owner in [${owners.map((o) => `'${o}'`).join(", ")}]`;
}
/** Bucket the project's Terraform keeps its state in. */
export function gcpStateBucket(projectId) {
    return `${projectId}-compbox-tfstate`;
}
/**
 * The Cloud Shell script. Safe to re-run: every step creates or updates.
 * Throws on any input that isn't a plain identifier, so nothing can be
 * injected into the script.
 */
export function buildGcpSetupScript(input) {
    if (!isGcpProjectId(input.projectId))
        throw new Error("Not a Google Cloud project ID.");
    if (!isGcpRegion(input.region))
        throw new Error("Not a supported region.");
    if (input.githubOwners.length === 0)
        throw new Error("Connect GitHub first.");
    for (const owner of input.githubOwners) {
        if (!isGitHubLogin(owner))
            throw new Error(`Not a GitHub account name: ${owner}`);
    }
    const owners = [...new Set(input.githubOwners)];
    const condition = gcpAttributeCondition(owners);
    const pool = `--location=global --workload-identity-pool=${GCP_IDENTITY_POOL}`;
    const oidc = [
        "--issuer-uri=https://token.actions.githubusercontent.com",
        '--attribute-mapping="google.subject=assertion.sub,attribute.repository=assertion.repository,attribute.repository_owner=assertion.repository_owner"',
        `--attribute-condition="${condition}"`,
    ].join(" ");
    return `# CompBox → Google Cloud (keyless). Paste into Google Cloud Shell.
set -euo pipefail
PROJECT=${input.projectId}
REGION=${input.region}
SA=${gcpServiceAccountEmail(input.projectId)}

gcloud config set project "$PROJECT"
gcloud services enable ${GCP_SERVICES.join(" ")}
NUMBER=$(gcloud projects describe "$PROJECT" --format='value(projectNumber)')

# The account deploys run as.
gcloud iam service-accounts describe "$SA" >/dev/null 2>&1 || \\
  gcloud iam service-accounts create ${GCP_DEPLOY_ACCOUNT} --display-name="CompBox deploys"
for ROLE in ${GCP_DEPLOY_ROLES.join(" ")}; do
  gcloud projects add-iam-policy-binding "$PROJECT" --member="serviceAccount:$SA" --role="$ROLE" --condition=None --quiet >/dev/null
done
# Lets deploys grant Cloud SQL Client to an environment's runtime account, and nothing else.
CONDITION=$(mktemp)
cat > "$CONDITION" <<'EOF'
title: ${GCP_DEPLOY_CONDITIONAL_ROLE.condition.title}
description: ${GCP_DEPLOY_CONDITIONAL_ROLE.condition.description}
expression: "${GCP_DEPLOY_CONDITIONAL_ROLE.condition.expression}"
EOF
gcloud projects add-iam-policy-binding "$PROJECT" --member="serviceAccount:$SA" \\
  --role="${GCP_DEPLOY_CONDITIONAL_ROLE.role}" --condition-from-file="$CONDITION" --quiet >/dev/null
rm -f "$CONDITION"

# Trust GitHub Actions from your GitHub only: ${owners.join(", ")}.
gcloud iam workload-identity-pools describe ${GCP_IDENTITY_POOL} --location=global >/dev/null 2>&1 || \\
  gcloud iam workload-identity-pools create ${GCP_IDENTITY_POOL} --location=global --display-name="CompBox"
if gcloud iam workload-identity-pools providers describe ${GCP_IDENTITY_PROVIDER} ${pool} >/dev/null 2>&1; then
  gcloud iam workload-identity-pools providers update-oidc ${GCP_IDENTITY_PROVIDER} ${pool} ${oidc}
else
  gcloud iam workload-identity-pools providers create-oidc ${GCP_IDENTITY_PROVIDER} ${pool} ${oidc}
fi
for OWNER in ${owners.join(" ")}; do
  gcloud iam service-accounts add-iam-policy-binding "$SA" --role=roles/iam.workloadIdentityUser --quiet >/dev/null \\
    --member="principalSet://iam.googleapis.com/projects/$NUMBER/locations/global/workloadIdentityPools/${GCP_IDENTITY_POOL}/attribute.repository_owner/$OWNER"
done

# Where each project's infrastructure state is kept.
gcloud storage buckets describe "gs://${gcpStateBucket(input.projectId)}" >/dev/null 2>&1 || \\
  gcloud storage buckets create "gs://${gcpStateBucket(input.projectId)}" --location="$REGION" --uniform-bucket-level-access

echo
echo "Done. Your project number is: $NUMBER"
echo "Paste it into CompBox to finish."
`;
}
/** The one-click Google Cloud setup, in the order CompBox runs it. */
export const GCP_CONNECT_STEPS = ["project", "services", "account", "identity", "trust", "storage"];
/** What the web shows while each step runs. */
export const GCP_CONNECT_STEP_LABELS = {
    project: "Checking your project",
    services: "Turning on Google Cloud services",
    account: "Creating the deploy account",
    identity: "Trusting your GitHub",
    trust: "Letting your GitHub deploy",
    storage: "Making a place for deploy state",
};
//# sourceMappingURL=cloud-setup.js.map